import {
  AUTHORITY_LABEL,
  ENV_LABEL,
  INVOICE_TYPE_LABEL,
  REG_TYPE_LABEL,
  STATUS_LABEL,
} from './di.constants';

export const DiAuthority = { Fbr: 1, Pra: 2 } as const;
export const DiInvoiceType = { SaleInvoice: 1, DebitNote: 2, CreditNote: 3 } as const;
export const DiInvoiceStatus = {
  Draft: 1,
  Validated: 2,
  Pending: 3,
  Accepted: 4,
  Failed: 5,
  Cancelled: 6,
} as const;
export const DiBuyerRegistrationType = { Registered: 1, Unregistered: 2 } as const;
export const DiEnvironment = { Sandbox: 1, Production: 2 } as const;
export const DiTransmissionStatus = {
  Pending: 1,
  Accepted: 2,
  Failed: 3,
  Cancelled: 4,
} as const;

export type DiAuthorityValue = (typeof DiAuthority)[keyof typeof DiAuthority];
export type DiInvoiceTypeValue = (typeof DiInvoiceType)[keyof typeof DiInvoiceType];
export type DiInvoiceStatusValue = (typeof DiInvoiceStatus)[keyof typeof DiInvoiceStatus];
export type DiBuyerRegistrationTypeValue =
  (typeof DiBuyerRegistrationType)[keyof typeof DiBuyerRegistrationType];
export type DiEnvironmentValue = (typeof DiEnvironment)[keyof typeof DiEnvironment];

export interface DiOption {
  value: number;
  label: string;
}

export const DI_AUTHORITY_OPTIONS: DiOption[] = [
  { value: DiAuthority.Fbr, label: AUTHORITY_LABEL[1] },
  { value: DiAuthority.Pra, label: AUTHORITY_LABEL[2] },
];

export const DI_INVOICE_TYPE_OPTIONS: DiOption[] = [
  { value: DiInvoiceType.SaleInvoice, label: INVOICE_TYPE_LABEL[1] },
  { value: DiInvoiceType.DebitNote, label: INVOICE_TYPE_LABEL[2] },
  { value: DiInvoiceType.CreditNote, label: INVOICE_TYPE_LABEL[3] },
];

export const DI_FBR_INVOICE_TYPE_OPTIONS: DiOption[] = [
  { value: DiInvoiceType.SaleInvoice, label: INVOICE_TYPE_LABEL[1] },
  { value: DiInvoiceType.DebitNote, label: INVOICE_TYPE_LABEL[2] },
];

export const DI_STATUS_OPTIONS: DiOption[] = [
  { value: DiInvoiceStatus.Draft, label: STATUS_LABEL[1] },
  { value: DiInvoiceStatus.Validated, label: STATUS_LABEL[2] },
  { value: DiInvoiceStatus.Pending, label: STATUS_LABEL[3] },
  { value: DiInvoiceStatus.Accepted, label: STATUS_LABEL[4] },
  { value: DiInvoiceStatus.Failed, label: STATUS_LABEL[5] },
  { value: DiInvoiceStatus.Cancelled, label: STATUS_LABEL[6] },
];

export const DI_REG_TYPE_OPTIONS: DiOption[] = [
  { value: DiBuyerRegistrationType.Registered, label: REG_TYPE_LABEL[1] },
  { value: DiBuyerRegistrationType.Unregistered, label: REG_TYPE_LABEL[2] },
];

export const DI_ENV_OPTIONS: DiOption[] = [
  { value: DiEnvironment.Sandbox, label: ENV_LABEL[1] },
  { value: DiEnvironment.Production, label: ENV_LABEL[2] },
];

function lookup(options: DiOption[], value: number | null | undefined): string {
  if (value == null) return '—';
  return options.find((o) => o.value === value)?.label ?? String(value);
}

export function diAuthorityLabel(v: number | null | undefined): string {
  return lookup(DI_AUTHORITY_OPTIONS, v);
}

export function diInvoiceTypeLabel(v: number | null | undefined): string {
  return lookup(DI_INVOICE_TYPE_OPTIONS, v);
}

export function diStatusLabel(v: number | null | undefined): string {
  return lookup(DI_STATUS_OPTIONS, v);
}

export function diRegTypeLabel(v: number | null | undefined): string {
  return lookup(DI_REG_TYPE_OPTIONS, v);
}

export function diEnvLabel(v: number | null | undefined): string {
  return lookup(DI_ENV_OPTIONS, v);
}

/** Editable statuses that reset to Draft on PUT. */
export function diCanEdit(status: number): boolean {
  return (
    status === DiInvoiceStatus.Draft ||
    status === DiInvoiceStatus.Validated ||
    status === DiInvoiceStatus.Pending ||
    status === DiInvoiceStatus.Failed
  );
}

export function diCanValidate(status: number): boolean {
  return diCanEdit(status);
}

export function diCanSubmit(status: number): boolean {
  return diCanEdit(status);
}

export function diCanCancel(status: number): boolean {
  return diCanEdit(status);
}

export function diIsImmutable(status: number): boolean {
  return status === DiInvoiceStatus.Accepted || status === DiInvoiceStatus.Cancelled;
}
